import {
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  debounce
} from '@mui/material';
import { API } from 'aws-amplify';
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import AssetOverviewTable from './components/AssetOverviewTable';
import RefreshIcon from '@mui/icons-material/Refresh';
import { userHasRoles } from '../../services/role-service';
import { Role } from '../../constants/roles';
import { MatchLogic } from '../../components/ProtectedRoute/ProtectedRoute';
import { SearchInput } from 'views/Marketing/components/SearchInput';

const BomOverview = props => {
  const [assets, setAssets] = useState([]);
  const [cells, setCells] = useState([]);
  const [parameters, setParameters] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [orderBy, setOrderBy] = React.useState('lastCheck');
  const [order, setOrder] = React.useState<'asc' | 'desc'>('desc');
  const [assetType, setAssetType] = useState('AIRCRAFT');
  const [restrictStatusChangeToGroundedOnly, setRestrictStatusChangeToGroundedOnly] = useState(false);
  const searchRef = useRef([]);

  useEffect(() => {
    userHasRoles([
      Role.Portal_Dev_Admin,
      Role.ROLE_DEPOT_MFG_ADMIN,
      Role.ROLE_DEPOT_MFG_USER,
      Role.ROLE_DEPOT_ENGINEERING_ADMIN,
      Role.ROLE_DEPOT_ENGINEERING_USER,
      Role.ROLE_DEPOT_OPS_ADMIN,
      Role.ROLE_DEPOT_OPS_USER,
      Role.ROLE_DEPOT_ADMIN
    ], MatchLogic.OR).then(hasFullAccess => {
      setRestrictStatusChangeToGroundedOnly(!hasFullAccess);
    })
  })

  useEffect(() => {
    loadAndSetAssets();
    loadAndSetParameters();
    loadAndSetCells();
  }, [API, assetType]);

  function loadAndSetAssets() {
    setIsLoading(true);
    API.get('bom', `/asset?assetType=` + assetType, {})
      .then(a => {
        searchRef.current = a;
        setAssets(a);
        setIsLoading(false);
      })
      .catch(e => {
        console.error(e);
        setIsLoading(false);
      });
  }

  function loadAndSetCells() {
    API.get('orderful-api', `/cells`, {})
    .then(res => {
      setCells(res);
    })
    .catch(err => {
      console.error(err);
    });
  }

  function loadAndSetParameters() {
    API.get('bom', `/parameter`, {})
      .then(p => {
        setParameters(p);
      })
      .catch(e => {
        console.error(e);
      });
  }

  function handleFilterChange(event) {
    setAssetType(event.target.value);
  }

  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  function getComparator<Key extends keyof any>(
    order: 'desc' | 'asc',
    orderBy: Key
  ): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string }
  ) => number {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property,
    order
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  function createNewIssue(assetId, note, issueType, severity, detail) {
    API.post('bom', `/issue`, {
      body: { assetId, note, issueType, severity, detail }
    });
  }

  const search = searchTerm => {
    const filteredResults = searchRef.current.filter(a => {
      if(assetType === "AIRCRAFT"){
        return a.assetName.includes(`MNA-`+searchTerm);
      } else if(assetType === "CASSETTE") {
        return a.assetName.includes(`CAS W`+searchTerm);
      }
    });
    setAssets(filteredResults);
  };

  const onSearch = debounce(search, 300);

  function createNewOverride(assetId, note, name, cellId, fcu1Value, fcu2Value, fcu3Value ) {
    API.post('bom', `/parameter/override`, {
      body: {
        assetId, note, name, cellId, fcu1Value, fcu2Value, fcu3Value }
    });
  }

  function deleteOverride(overrideId) {
    API.get('bom', `/parameter/override/delete/${overrideId}`,{});
  }

  function updateStatus(assetId, state, reason) {
    const asset = assets.find(a => a.id === assetId);
    const body = Object.assign({}, asset, { state, reason, assetId });
    API.post('bom', `/asset/state`, { body })
      .then(res => {
        // reload the table
        loadAndSetAssets();
      })
      .catch(e => {
        console.error(e);
      });
  }

  return (
    <div
      style={{
        padding: 12
      }}>
      <FormControl>
        <InputLabel>Asset Filter</InputLabel>
        <Select
          value={assetType}
          label="AIRCRAFT"
          onChange={handleFilterChange}>
          <MenuItem value={'AIRCRAFT'}>AIRCRAFT</MenuItem>
          <MenuItem value={'CASSETTE'}>CASSETTE</MenuItem>
          <MenuItem value={'SPOOL'}>SPOOL</MenuItem>
          <MenuItem value={'CHARGER'}>CHARGER</MenuItem>
          <MenuItem value={'BATTERY'}>BATTERY</MenuItem>
          <MenuItem value={'RC'}>RC</MenuItem>
          <MenuItem value={'TABLET'}>TABLET</MenuItem>
        </Select>
      </FormControl>

      <Tooltip title="Refresh">
        <IconButton color="primary" onClick={loadAndSetAssets}>
          <RefreshIcon />
        </IconButton>
      </Tooltip>
      {(assetType === "AIRCRAFT" || assetType ===  "CASSETTE") && <span style={{display:"inline-block", marginTop:10}}><SearchInput onSearch={onSearch} searchText={assetType} /></span>}
      <AssetOverviewTable
        restrictStatusChangeToGroundedOnly={restrictStatusChangeToGroundedOnly}
        onRequestSort={handleRequestSort}
        onRequestStatusChange={(id, state, reason) =>
          updateStatus(id, state, reason)
        }
        onRequestNewIssue={(id, data) => {
          createNewIssue(
            id,
            data.note,
            data.issueType,
            data.issueSeverity,
            data.detail
          );
        }}
        onRequestNewOverride={(id, data) => {
          createNewOverride(
            id,
            data.note,
            data.name,
            data.cellId,
            data.fcu1Value,
            data.fcu2Value,
            data.fcu3Value
          );
        }}
        onRequestDeleteOverride={(id) => {
          deleteOverride(
            id
          );
        }}
        assets={assets.slice().sort(getComparator(order, orderBy))}
        parameters={parameters}
        cells={cells}
        isLoading={isLoading}
      />
    </div>
  );
};

const mapStateToProps = state => ({});
const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(BomOverview);
